import classNames from 'classnames';
import React from 'react';

interface FhToggleProps {
	options: string[];
	active: string;
	onChange: (selected: string) => void;
	icons?: Record<string, React.ReactNode>;
	/** A figure shown muted after an option's label, e.g. how many entries it holds. */
	counts?: Record<string, React.ReactNode>;
	/** Set when the toggle filters a list: it then reads as tabs to a screen reader, under this name. */
	ariaLabel?: string;
	/** A `data-test-id` per option. */
	testIdFor?: (option: string) => string;
}

const defaultIcons: Record<string, React.ReactNode> = {
	Bar: (
		<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
			<rect x="1" y="8" width="4" height="7" rx="0.5" fill="currentColor" />
			<rect x="6" y="4" width="4" height="11" rx="0.5" fill="currentColor" />
			<rect x="11" y="1" width="4" height="14" rx="0.5" fill="currentColor" />
		</svg>
	),
	Line: (
		<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M1 12L5 7L9 9L15 3"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	),
	Area: (
		<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M1 12L5 7L9 9L15 3V15H1V12Z" fill="currentColor" opacity="0.3" />
			<path
				d="M1 12L5 7L9 9L15 3"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	),
	Pie: (
		<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M8 1A7 7 0 1 1 1 8h7V1z" fill="currentColor" />
			<path d="M9 0v6h6A7 7 0 0 0 9 0z" fill="currentColor" opacity="0.4" />
		</svg>
	),
};

function FhToggle({ options, active, onChange, icons, counts, ariaLabel, testIdFor }: FhToggleProps) {
	const iconMap = icons ?? defaultIcons;

	return (
		<div className="fh-toggle" role={ariaLabel ? 'tablist' : undefined} aria-label={ariaLabel}>
			{options.map(option => {
				const icon = iconMap[option];
				return (
					<button
						type="button"
						key={option}
						onClick={() => onChange(option)}
						role={ariaLabel ? 'tab' : undefined}
						aria-selected={ariaLabel ? active === option : undefined}
						data-test-id={testIdFor?.(option)}
						className={classNames('fh-toggle__button', {
							'fh-toggle__button--active': active === option,
							// Marks the buttons whose label may be dropped in favour of the icon on a
							// narrow screen. Only an option with an icon qualifies — without this,
							// text-only toggles (By day / By user, Booking type / Payment type, …)
							// hid their labels too and rendered as blank boxes that didn't read as
							// buttons at all. See fh-toggle.scss.
							'fh-toggle__button--has-icon': icon !== undefined,
						})}
						title={option}
					>
						{icon !== undefined && <span className="fh-toggle__icon">{icon}</span>}
						<span className="fh-toggle__label">{option}</span>
						{counts?.[option] !== undefined && <span className="fh-toggle__count">{counts[option]}</span>}
					</button>
				);
			})}
		</div>
	);
}

export default FhToggle;
